import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Event details
  eventDetails: null,
  
  // Selected items
  selectedService: null,
  selectedOptionalServices: [],
  selectedTimeOption: null,
  
  // Devotee (user) information
  devoteeInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    relationship: 'Self',
    gothram: '',
    nastram: '',
  },
  
  // Family members (similar to students in class module)
  familyMembers: [],
  selectedFamilyMemberIds: [],
  registeredMemberIds: [], // IDs of family members already registered for this event
  
  // Price calculation
  totalPrice: 0,
  
  // Booking flow state
  currentStep: 'details', // details, overview, payment, success
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    // Set event details
    setEventDetails: (state, action) => {
      state.eventDetails = action.payload;
    },
    
    // Select service (single selection)
    selectService: (state, action) => {
      state.selectedService = action.payload;
      bookingSlice.caseReducers.calculateTotal(state);
    },
    
    // Toggle optional service (multiple selection)
    toggleOptionalService: (state, action) => {
      const service = action.payload;
      const index = state.selectedOptionalServices.findIndex(
        s => s.optional_Service_Id === service.optional_Service_Id
      );
      
      if (index >= 0) {
        // Remove if already selected
        state.selectedOptionalServices.splice(index, 1);
      } else {
        // Add if not selected
        state.selectedOptionalServices.push(service);
      }
      
      bookingSlice.caseReducers.calculateTotal(state);
    },
    
    // Select time option (single selection)
    selectTimeOption: (state, action) => {
      state.selectedTimeOption = action.payload;
    },
    
    // Update devotee info
    updateDevoteeInfo: (state, action) => {
      state.devoteeInfo = { ...state.devoteeInfo, ...action.payload };
    },
    
    // Set devotee info (from API)
    setDevoteeInfo: (state, action) => {
      state.devoteeInfo = action.payload;
    },
    
    // Set family members (from API)
    setFamilyMembers: (state, action) => {
      state.familyMembers = action.payload;
    },
    
    // Add family member
    addFamilyMember: (state, action) => {
      state.familyMembers.push(action.payload);
    },
    
    // Remove family member
    removeFamilyMember: (state, action) => {
      state.familyMembers = state.familyMembers.filter(
        (member) => member.id !== action.payload
      );
    },
    
    // Update family member
    updateFamilyMember: (state, action) => {
      const { id, data } = action.payload;
      const index = state.familyMembers.findIndex(m => m.id === id);
      if (index >= 0) {
        state.familyMembers[index] = { ...state.familyMembers[index], ...data };
      }
    },
    
    // Toggle family member selection
    toggleFamilyMemberSelection: (state, action) => {
      const memberId = action.payload;
      const index = state.selectedFamilyMemberIds.indexOf(memberId);
      if (index >= 0) {
        state.selectedFamilyMemberIds.splice(index, 1);
      } else {
        state.selectedFamilyMemberIds.push(memberId);
      }
      // Recalculate total when selection changes
      bookingSlice.caseReducers.calculateTotal(state);
    },
    
    // Set registered member IDs (members already registered for this event)
    setRegisteredMemberIds: (state, action) => {
      state.registeredMemberIds = action.payload;
    },
    
    // Calculate total price
    calculateTotal: (state) => {
      let totalPrice = 0;
      const attendeeCount = 1 + state.selectedFamilyMemberIds.length;
      
      // Add service price based on pricing model
      if (state.selectedService && !state.selectedService.is_Free) {
        const servicePrice = state.selectedService.service_Price;
        const pricingModel = state.selectedService.pricing_Model || state.selectedService.Pricing_Model || 'PER_PERSON';
        
        console.log('🔍 Service Pricing Debug:', {
          serviceName: state.selectedService.service_Name,
          servicePrice,
          pricingModel,
          attendeeCount,
          rawService: state.selectedService
        });
        
        if (pricingModel === 'FLAT') {
          // Fixed price regardless of attendees
          console.log('✅ Using FLAT pricing:', servicePrice);
          totalPrice += servicePrice;
        } else {
          // PER_PERSON or default: multiply by attendee count
          console.log('✅ Using PER_PERSON pricing:', servicePrice, '×', attendeeCount, '=', servicePrice * attendeeCount);
          totalPrice += servicePrice * attendeeCount;
        }
      }
      
      // Add optional services prices based on pricing model
      state.selectedOptionalServices.forEach(service => {
        if (!service.is_Free) {
          const optionalPrice = service.optional_Service_Price;
          const pricingModel = service.pricing_Model || service.Pricing_Model || 'PER_PERSON';
          
          if (pricingModel === 'FLAT') {
            // Fixed price regardless of attendees
            totalPrice += optionalPrice;
          } else {
            // PER_PERSON or default: multiply by attendee count
            totalPrice += optionalPrice * attendeeCount;
          }
        }
      });
      
      state.totalPrice = totalPrice;
    },
    
    // Set current step
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    
    // Reset booking
    resetBooking: (state) => {
      return initialState;
    },
    
    // Clear selections only (keep event details)
    clearSelections: (state) => {
      state.selectedService = null;
      state.selectedOptionalServices = [];
      state.selectedTimeOption = null;
      state.devoteeInfo = initialState.devoteeInfo;
      state.familyMembers = [];
      state.selectedFamilyMemberIds = [];
      state.registeredMemberIds = [];
      state.totalPrice = 0;
      state.currentStep = 'details';
    },
  },
});

export const {
  setEventDetails,
  selectService,
  toggleOptionalService,
  selectTimeOption,
  updateDevoteeInfo,
  setDevoteeInfo,
  setFamilyMembers,
  addFamilyMember,
  removeFamilyMember,
  updateFamilyMember,
  toggleFamilyMemberSelection,
  setRegisteredMemberIds,
  calculateTotal,
  setCurrentStep,
  resetBooking,
  clearSelections,
} = bookingSlice.actions;

export default bookingSlice.reducer;
